// next-app/src/hooks/use-dealer.ts
'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { useAuthStore } from '@/store/auth-store';
import { queryKeys } from './query-keys';
import type { Dealer } from '@/types';

// ============================================================================
// Get Dealer Query
// ============================================================================

/**
 * Hook to fetch current dealer information.
 *
 * Original: GET v1/event/dealer
 *
 * Note: Usually the dealer is already in auth store from login.
 * This is for refreshing dealer data or getting additional fields.
 */
export function useDealer() {
  const token = useAuthStore((state) => state.dealerToken);
  const setDealer = useAuthStore((state) => state.setDealer);

  return useQuery({
    queryKey: queryKeys.dealer.current(),
    queryFn: async () => {
      const { response, result } = await api.getDealer();

      if (!response.ok) {
        throw new Error('Failed to fetch dealer');
      }

      const data = result as { dealer: Dealer };

      // Keep auth store in sync
      setDealer(data.dealer);

      return data;
    },
    enabled: Boolean(token),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ============================================================================
// Update Dealer Mutation
// ============================================================================

interface UseUpdateDealerOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to update dealer profile.
 *
 * Original: POST v1/auth/updateDealer
 *
 * Updates both the server and local auth store.
 */
export function useUpdateDealer(options: UseUpdateDealerOptions = {}) {
  const queryClient = useQueryClient();
  const setDealer = useAuthStore((state) => state.setDealer);

  return useMutation({
    mutationFn: async (dealer: Dealer) => {
      const { response, result } = await api.updateDealer(dealer);

      if (!response.ok) {
        throw new Error('Failed to update dealer profile');
      }

      return result as { dealer: Dealer };
    },
    onSuccess: (data) => {
      // Update auth store with new dealer info
      setDealer(data.dealer);

      // Invalidate dealer queries
      queryClient.invalidateQueries({ queryKey: queryKeys.dealer.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.account() });

      options.onSuccess?.();
    },
    onError: (error: Error) => {
      console.error('[useUpdateDealer] Error:', error.message);
      options.onError?.(error);
    },
  });
}
